import React, { useState, useRef, useEffect } from 'react';
import './WhitePane.css';
import CheckoutModal from '../CheckoutModal';

const WhitePane = ({ onSelect, listing }) => {
    const [selected, setSelected] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const paypalButtonRef = useRef(null);

    // Detect mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 600);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleSelect = (option) => {
        setSelected(option);
        onSelect(option);
    };

    const handleExpand = (expanded) => {
        // Never expand on mobile
        if (isMobile) {
            console.log('Expansion blocked on mobile');
            return;
        }
        console.log('WhitePane expanding:', expanded);
        setIsExpanded(expanded);
    };

    // Function to smoothly scroll to PayPal button
    const scrollToPayPal = () => {
        // On mobile, just scroll to the checkout area without expanding
        if (isMobile) {
            const checkoutModal = document.querySelector('.checkout-modal');
            if (checkoutModal) {
                checkoutModal.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
            return;
        }
        
        // Desktop behavior
        const checkoutModal = document.querySelector('.checkout-modal');
        if (checkoutModal) {
            const rect = checkoutModal.getBoundingClientRect();
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            const targetScroll = currentScroll + rect.top - 50; // 50px offset from top
            
            window.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
            });
        }
    };

    // Destructure cert from listing for easier access
    const { cert } = listing || {};

    console.log('isMobile state:', isMobile, 'window width:', window.innerWidth);

    return (
        <div className={`white-pane ${isExpanded ? 'expanded' : ''}`}>
            <div className="white-pane-header">Shop Confident.</div>
            <div className="selection-container">
                <a
                    href={cert?.cert_number ? `https://www.psacard.com/cert/${cert.cert_number}/psa` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div 
                        className={`selection-box ${selected === 'collectability' ? 'active' : ''}`}
                        style={{ position: 'relative' }}
                    >
                        <div className="selection-content">
                            <div className="selection-header">
                                <div className="step-number">1</div>
                                <h3>See Your Certificate</h3>
                            </div>
                            <p>See your cert on PSA's site.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <img 
                                src={cert?.cert_images?.front_image_url || 'https://via.placeholder.com/80x110'} 
                                alt="Card Front" 
                                className="selection-image"
                            />
                            <img 
                                src={cert?.cert_images?.back_image_url || 'https://via.placeholder.com/80x110'} 
                                alt="Card Back" 
                                className="selection-image"
                            />
                        </div>
                        <span className="external-arrow" aria-label="External link" title="Opens in new tab">
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 11H16" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                                <path d="M12 7L16 11L12 15" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                            </svg>
                        </span>
                    </div>
                </a>
                <a
                    href={cert?.price_charting_id ? `https://www.pricecharting.com/game/${cert.price_charting_id}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div 
                        className={`selection-box ${selected === 'protection' ? 'active' : ''}`}
                        style={{ position: 'relative' }}
                    >
                        <div className="selection-content">
                            <div className="selection-header">
                                <div className="step-number">2</div>
                                <h3>Check Your Price</h3>
                            </div>
                            <p>Check PriceCharting estimates.</p>
                        </div>
                        <div className="pricecharting-widget" style={{ 
                            margin: 0,
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <h3 style={{ margin: '0 0 4px 0', textAlign: 'center' }}>PriceCharting</h3>
                            {cert?.pricing_data ? (
                                <>
                                    <div className="price-row" style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: '55px 15px auto',
                                        gap: '5px',
                                        alignItems: 'center',
                                        margin: '2px 0'
                                    }}>
                                        <span className="label" style={{ textAlign: 'left' }}>PSA 10</span>
                                        <span className="colon" style={{ textAlign: 'center' }}>:</span>
                                        <span className="price" style={{ textAlign: 'right' }}>
                                            ${cert.pricing_data.psa_10 ? cert.pricing_data.psa_10.toFixed(0) : '--'}
                                        </span>
                                    </div>
                                    <div className="price-row" style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: '55px 15px auto',
                                        gap: '5px',
                                        alignItems: 'center',
                                        margin: '2px 0'
                                    }}>
                                        <span className="label" style={{ textAlign: 'left' }}>PSA 9</span>
                                        <span className="colon" style={{ textAlign: 'center' }}>:</span>
                                        <span className="price" style={{ textAlign: 'right' }}>
                                            ${cert.pricing_data.psa_9 ? cert.pricing_data.psa_9.toFixed(0) : '--'}
                                        </span>
                                    </div>
                                    <div className="price-row" style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: '55px 15px auto',
                                        gap: '5px',
                                        alignItems: 'center',
                                        margin: '2px 0'
                                    }}>
                                        <span className="label" style={{ textAlign: 'left' }}>PSA 8</span>
                                        <span className="colon" style={{ textAlign: 'center' }}>:</span>
                                        <span className="price" style={{ textAlign: 'right' }}>
                                            ${cert.pricing_data.psa_8 ? cert.pricing_data.psa_8.toFixed(0) : '--'}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    No data
                                </div>
                            )}
                        </div>
                        <span className="external-arrow" aria-label="External link" title="Opens in new tab">
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 11H16" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                                <path d="M12 7L16 11L12 15" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                            </svg>
                        </span>
                    </div>
                </a>
                <a
                    href={`/listing/${cert?.cert_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div 
                        className={`selection-box ${selected === 'value' ? 'active' : ''}`}
                        style={{ position: 'relative', cursor: 'pointer' }}
                    >
                        <div className="selection-content">
                            <div className="selection-header">
                                <div className="step-number">3</div>
                                <h3>Check Out Securely</h3>
                            </div>
                            <p>Check out with PayPal.</p>
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            justifyContent: 'center',
                            width: '80px',
                            height: '110px',
                            paddingTop: '10px',
                            transform: isMobile ? 'translateY(-10px)' : 'translateY(0px)'
                        }}>
                            <span className="deal-grade-badge homepage-badge" data-grade="A+" style={{
                                position: 'relative',
                                left: 'auto',
                                bottom: 'auto',
                                width: isMobile ? '60px' : '80px',
                                height: isMobile ? '60px' : '80px',
                                fontSize: '1.2rem',
                                borderWidth: '2px'
                            }}>
                                <span className="deal-grade-letter" style={{ fontSize: isMobile ? '1.8rem' : '2.5rem' }}>A+</span>
                                <span className="deal-grade-label" style={{ fontSize: isMobile ? '0.7rem' : '1rem' }}>DEAL</span>
                            </span>
                        </div>
                        <span className="external-arrow" aria-label="External link" title="Opens in new tab">
                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 11H16" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                                <path d="M12 7L16 11L12 15" stroke="#B0B0B0" stroke-width="2.2" stroke-linecap="round"/>
                            </svg>
                        </span>
                    </div>
                </a>
            </div>
            <div className="white-pane-header">Shop Quick.</div>

            <CheckoutModal listing={listing} onExpand={handleExpand} isExpanded={isExpanded} />
        </div>
    );
};

export default WhitePane;